import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-markdown-input.js";
import { LitElement, css, html } from "lit";
import { property, query } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import "@progressive-development/pd-forms/pd-button";
import "@progressive-development/pd-forms/pd-input";
//#region src/pd-chat/PdChatInput.ts
/**
* @license
* Copyright (c) 2026 PD Progressive Development UG. All rights reserved.
*/
/**
* valueName des inneren Formular-Elements. Bewusst eindeutig: sein
* `pd-form-element-unregister` geht auf `document` und lässt sich an der
* Komponenten-Grenze nicht abfangen — ein unbekannter Name macht es in
* jedem umgebenden `pd-form-container` zum No-op.
*/
var INPUT_NAME = "__pdchat_input";
var PdChatInput = class PdChatInput extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.variant = "simple";
		this.disabled = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
    }

    .row {
      display: flex;
      gap: var(--pd-chat-input-gap, 0.5rem);
      align-items: center;
    }

    .row pd-input {
      flex: 1;
      min-width: 0;
      --pd-input-field-width: 100%;
    }

    .stack {
      display: flex;
      flex-direction: column;
      gap: var(--pd-chat-input-gap, 0.5rem);
    }

    .actions {
      display: flex;
      justify-content: flex-end;
    }
  `;
	}
	render() {
		return html`
      <div
        role="group"
        aria-label="${this._placeholder()}"
        @pd-form-element-register="${this._swallow}"
        @pd-form-element-unregister="${this._swallow}"
        @pd-form-element-change="${this._swallow}"
        @pd-form-element-blur="${this._swallow}"
        @pd-form-element-focus="${this._swallow}"
      >
        ${this.variant === "rich" ? this._renderRich() : this._renderSimple()}
      </div>
    `;
	}
	/** Einzeilig: Enter sendet — das gewohnte Verhalten eines Chatfelds. */
	_renderSimple() {
		return html`
      <div class="row" part="row">
        <pd-input
          valueName="${INPUT_NAME}"
          .placeHolder="${this._placeholder()}"
          .initValue="${this.draft ?? ""}"
          ?disabled="${this.disabled}"
          @enter-pressed="${this._handleSubmit}"
        ></pd-input>
        ${this._renderSubmit()}
      </div>
    `;
	}
	/**
	* Markdown: Enter erzeugt hier einen Absatz, nicht das Absenden — in
	* einem Editor wäre Senden-auf-Enter falsch. Gesendet wird nur über die
	* Schaltfläche.
	*/
	_renderRich() {
		return html`
      <div class="stack" part="row">
        <pd-markdown-input
          valueName="${INPUT_NAME}"
          .placeHolder="${this._placeholder()}"
          .initValue="${this.draft ?? ""}"
          .rows="${this.richInput?.rows ?? 3}"
        ></pd-markdown-input>
        <div class="actions">${this._renderSubmit()}</div>
      </div>
    `;
	}
	_renderSubmit() {
		return html`
      <pd-button
        primary
        .text="${this.submitLabel ?? msg("Senden", { id: "pd.chat.input.submit" })}"
        ?disabled="${this.disabled}"
        @click="${this._handleSubmit}"
      ></pd-button>
    `;
	}
	_placeholder() {
		return this.placeHolder ?? msg("Nachricht schreiben …", { id: "pd.chat.input.placeholder" });
	}
	/**
	* `toolbarActions` wird imperativ gesetzt statt gebunden: pd-markdown-input
	* hat eine eigene Vollausstattung als Default, den eine Bindung mit
	* `undefined` überschreiben würde.
	*/
	updated(changed) {
		if (!changed.has("richInput") && !changed.has("variant")) return;
		const actions = this.richInput?.toolbarActions;
		if (!actions || this.variant !== "rich") return;
		const editor = this._input;
		if (editor) editor.toolbarActions = actions;
	}
	/**
	* Komponenten-Grenze: ein umgebender `pd-form-container` registriert
	* JEDES bubbelnde `pd-form-element-*` ungefiltert. Der Entwurfstext darf
	* diese Komponente nie als Formularfeld verlassen.
	*/
	_swallow(e) {
		e.stopPropagation();
	}
	/** Leerer Text sendet nicht — bewusst ohne Fehlermeldung und ohne
	*  deaktivierte Schaltfläche: pd-input entprellt Tastatureingaben um
	*  500 ms, ein daran gekoppelter Button-Zustand liefe sichtbar nach. */
	_handleSubmit() {
		if (this.disabled) return;
		const text = (this._input?.value ?? "").trim();
		if (!text) return;
		this.dispatchEvent(new CustomEvent("pd-chat-submit", {
			detail: { text },
			bubbles: true,
			composed: true
		}));
		this._input?.reset();
	}
};
__decorate([property()], PdChatInput.prototype, "variant", void 0);
__decorate([property()], PdChatInput.prototype, "placeHolder", void 0);
__decorate([property({ type: Boolean })], PdChatInput.prototype, "disabled", void 0);
__decorate([property()], PdChatInput.prototype, "submitLabel", void 0);
__decorate([property()], PdChatInput.prototype, "draft", void 0);
__decorate([property({ attribute: false })], PdChatInput.prototype, "richInput", void 0);
__decorate([query("pd-input, pd-markdown-input")], PdChatInput.prototype, "_input", void 0);
PdChatInput = __decorate([localized()], PdChatInput);
//#endregion
export { PdChatInput };
